import store from "@/store";
import constants, {
  baseURL,
  COLORS_HEX,
  URL_DEFAULT_ICON_RECURSO,
} from "../constants";
import { loadModules } from "esri-loader";
import { wrapText } from "../FormattersHelper";
import { getEmergenciaColor } from "../EmergenciaHelper";
import * as ArcGIS from '../../helpers/ArcGIS'

export const buildLayers = async (layerTree, mapInstance) => {
  const contornoPromise = createGraphicsContorno().catch((err) => {
    console.error("Error precomputing contorno:", err);
    return null;
  });

  await traverseLayers(layerTree, async (node) => {
    await createLayerFromNode(node, mapInstance);

    if (node.layer?.id === "layer-contorno") {
      const contornoGraphic = await contornoPromise;
      if (contornoGraphic) {
        await applyContornoGraphics(mapInstance, contornoGraphic);
      }
    }
  });

  const orderedLayers = mapInstance.layers
    .toArray()
    .sort((a, b) => a.position - b.position);

  orderedLayers.forEach((layer, index) => {
    mapInstance.reorder(layer, index);
  });

  reordenarLayersViento(mapInstance)
};

let definitionExpressionRecursos
export const setDefinitionExpressionRecursos = (expression) => {
  definitionExpressionRecursos = expression
}

const reordenarLayersViento = (mapInstance) => {
  const layerViento = mapInstance.findLayerById('layer-viento')
  const layerVientoLineas = mapInstance.findLayerById('layer-viento-lineas')

  if (layerViento && layerVientoLineas) {
    const layerSim = mapInstance.findLayerById('layer-simulacion')
    const simIndex = mapInstance.layers.indexOf(layerSim)
    mapInstance.reorder(layerViento, simIndex + 2)
    mapInstance.reorder(layerVientoLineas, simIndex + 2)
  }
}

const traverseLayers = async (nodes, handler) => {
  const promises = nodes.map(async (node) => {
    if (node.type === "group" && node.children?.length) {
      return traverseLayers(node.children, handler);
    }

    if (node.type === "layer" && node.layer) {
      return handler(node);
    }

    return null;
  });

  await Promise.all(promises);
};

const createLayerFromNode = async (node, mapInstance) => {
  const layerConfig = node.layer;
  let layer = null;

    switch (layerConfig.type) {

        case 'graphicsLayer':
            layer = await createGraphicsLayer(layerConfig)
            break

        case 'GeoJSON':
            try {
              layerConfig.url = `${baseURL}/layers-static/${layerConfig.url}.geojson`;
              layer = await createGeoJsonLayer(layerConfig);
            } catch (err) {
              console.error(`No se pudo cargar ${layerConfig.id}`, err);
              return;
            }
            break

        case 'WFS':
            if (layerConfig.id === 'layer-recursos') {
              if (!definitionExpressionRecursos) {
                const tipos = constants.iconosFilterISSI.map(f => f.TIPO)

                const tiposSQL = tipos.length ? `'${tipos.join("','")}'` : null
                const filtro = tiposSQL ? `(ID_RECURSO IS NOT NULL AND TIPO NOT IN (${tiposSQL}))` : `(ID_RECURSO IS NOT NULL)`
                definitionExpressionRecursos = ` AND (${filtro} OR 1=0)`
              }

              layerConfig.definitionExpression =  `(1=0 OR ACTIVO = 1) ${definitionExpressionRecursos}`

              store.dispatch('layer/setDefinitionExpression', {
                id: 'layer-recursos',
                definitionExpression: definitionExpressionRecursos
              })
            }
            layer = await createWFS(layerConfig)
            break

        case 'WMS':
            layer = await createWMSLayer(layerConfig)
            break

        case 'MediaLayer':
            layer = await createMediaLayer(layerConfig)
            break

        case "featureLayer":
            layer = await createFeatureLayer(layerConfig);
            break;

        case 'ARCGIS':
            layer = await createMapImageLayer(layerConfig);
            break;

        case 'vectorTileLayer':
            layer = await createVectorTileLayer(layerConfig)
            break
    }

  if (!layer) return;
  mapInstance.add(layer, layer.position);

  if (layerConfig.id === "layer-infraestructuras-propias") {
    await ArcGIS.redrawInfraestructuras()
  }
};

const createMapImageLayer = async (config) => {
  await ArcGIS.ensureArcGISToken();
  const [MapImageLayer] = await loadModules(["esri/layers/MapImageLayer"]);

  const sublayers = (config.layers || []).map((l) => ({
    id: l.name,
    title: l.title,
  }));

  return new MapImageLayer({
    id: config.id,
    url: config.url,
    visible: config.visible,
    opacity: config.opacity,
    position: config.position,
    sublayers: sublayers.length ? sublayers : undefined,
  });
};

const createWMSLayer = async (config) => {
  const [WMSLayer] = await loadModules(["esri/layers/WMSLayer"]);

  const sublayers = (config.layers || []).map((l) => ({
    name: l.name,
    title: l.title,
  }));

  const layer = new WMSLayer({
    id: config.id,
    url: config.url,
    version: config.version || "1.3.0",
    visible: config.visible,
    opacity: config.opacity,
    position: config.position,
    sublayers,
  });

  return layer;
};

async function createWFS(layerItem) {
    try {
        const [WFSLayer] = await loadModules(['esri/layers/WFSLayer'])

        return new WFSLayer({
            id: layerItem.id,
            url: layerItem.url,
            name: layerItem.layers?.[0]?.name,
            title: layerItem.title,
            visible: layerItem.visible,
            opacity: layerItem.opacity,
            renderer: await renderRecursoLayer(),
            labelingInfo: await featureLayerlabels('ALIAS'),
            definitionExpression: layerItem.definitionExpression,
            labelsVisible: layerItem.labelsVisible,
            outFields: ['*'],
            legendEnabled: layerItem.legendEnabled !== undefined ? layerItem.legendEnabled : true, // al ser un boolean se necesita poner el distinto de undefined
            orderBy: layerItem.orderBy,
            refreshInterval: layerItem.refreshInterval,
            position: layerItem.position
        })
    } catch (err) {
        console.error(err)
        throw err
    }
}

async function renderRecursoLayer() {
  const [UniqueValueRenderer, PictureMarkerSymbol, Color] = await loadModules([
    "esri/renderers/UniqueValueRenderer",
    "esri/symbols/PictureMarkerSymbol",
    "esri/Color",
  ]);

  const renderer = new UniqueValueRenderer({
    valueExpression: "$feature.ICON + '|' + $feature.COLOR",
    defaultSymbol: new PictureMarkerSymbol({
      url: URL_DEFAULT_ICON_RECURSO, // URL por defecto si no hay coincidencia
      width: "40px",
      height: "40px",
    }),
  });

  const iconoSources = store.getters["icono/iconoSources"];

  const recursoSectorEstadoTipos = store.getters["recurso/recursoEstadoTipos"];

  const radius = 20;
  const segments = 60;
  const angleStep = (2 * Math.PI) / segments;
  const ring = [];

  for (let i = 0; i <= segments; i++) {
    const angle = i * angleStep;
    const x = radius * Math.cos(angle);
    const y = radius * Math.sin(angle);
    ring.push([x, y]);
  }

    recursoSectorEstadoTipos?.forEach(recursoSectorEstadoTipo => {
        const color = Color.fromHex(recursoSectorEstadoTipo.COLOR).toRgba()
        color[3] = 255 // La opacidad se necesita de 0 a 255
        iconoSources.forEach(source => {
            renderer.addUniqueValueInfo({
                value: `${source.key}|${recursoSectorEstadoTipo.COLOR}`,
                symbol: recursoLayerSymbol(source.url, color, ring, radius)
            })
        })
    })

  // Color activo

  const colorActivo = Color.fromHex(COLORS_HEX.NEGRO).toRgba();
  colorActivo[3] = 255; // La opacidad se necesita de 0 a 255

    iconoSources.forEach(source => {
        renderer.addUniqueValueInfo({
            value: `${source.key}|${COLORS_HEX.NEGRO}`,
            symbol: recursoLayerSymbol(source.url, colorActivo, ring, radius)
        })
    })

    return renderer
}

function recursoLayerSymbol(iconUrl, color, ring, radius) {
  return {
    type: "cim",
    data: {
      type: "CIMSymbolReference",
      symbol: {
        type: "CIMPointSymbol",
        symbolLayers: [
          {
            type: "CIMPictureMarker",
            enable: true,
            url: iconUrl,
            size: 30,
            verticalAlignment: "middle",
            horizontalAlignment: "center",
          },
          {
            type: "CIMVectorMarker",
            enable: true,
            size: 26,
            frame: {
              xmin: -radius,
              ymin: -radius,
              xmax: radius,
              ymax: radius,
            },
            markerGraphics: [
              {
                type: "CIMMarkerGraphic",
                geometry: {
                  rings: [ring],
                },
                symbol: {
                  type: "CIMPolygonSymbol",
                  symbolLayers: [
                    {
                      type: "CIMSolidFill",
                      enable: true,
                      color,
                    },
                  ],
                },
              },
            ],
            anchorPoint: { x: 0, y: 0 },
            anchorPointUnits: "Relative",
            respectFrame: true,
          },
        ],
      },
    },
  };
}

async function featureLayerlabels(labelAtribute) {
  const [LabelClass] = await loadModules(["esri/layers/support/LabelClass"]);

  const labelClassConAlerta = new LabelClass({
    labelExpressionInfo: {
      expression: `$feature.${labelAtribute}`,
    },
    labelPlacement: "below-center",
    where: `${labelAtribute} LIKE '%!%'`,
    symbol: {
      type: "text",
      color: "red", // texto rojo si contiene '!'
      haloColor: "white",
      haloSize: "2px",
      font: {
        size: 8,
        family: "Arial Unicode MS",
        weight: "bold",
      },
    },
  });

  // LabelClass cuando ALIAS NO contiene '!'
  const labelClassSinAlerta = new LabelClass({
    labelExpressionInfo: {
      expression: `$feature.${labelAtribute}`,
    },
    labelPlacement: "below-center",
    where: `${labelAtribute} NOT LIKE '%!%' OR ${labelAtribute} IS NULL`,
    symbol: {
      type: "text",
      color: "black", // texto negro si no contiene '!'
      haloColor: "white",
      haloSize: "2px",
      font: {
        size: 8,
        family: "Arial Unicode MS",
        weight: "bold",
      },
    },
  });
  return [labelClassConAlerta, labelClassSinAlerta];
}

async function createGraphicsLayer(layerItem) {
  try {
    const [GraphicsLayer] = await loadModules(["esri/layers/GraphicsLayer"]);

    const layer = new GraphicsLayer({
      id: layerItem.id,
      title: layerItem.title,
      visible: layerItem.visible,
      opacity: layerItem.opacity,
      position: layerItem.position,
    });

    return layer;
  } catch (err) {
    console.error(err);
  }
}

async function createVectorTileLayer(layerItem) {
  try {
    const [VectorTileLayer] = await loadModules([
      "esri/layers/VectorTileLayer"
    ]);

    const layer = new VectorTileLayer({
      id: layerItem.id,
      title: layerItem.title,
      url: layerItem.url,
      visible: layerItem.visible,
      opacity: layerItem.opacity,
      position: layerItem.position
    });

    await layer.load();

    return layer;
  } catch (error) {
    console.error("Error creando VectorTileLayer", error);
    throw error;
  }
}

async function createMediaLayer(layerItem) {
  try {
    const [MediaLayer] = await loadModules(["esri/layers/MediaLayer"]);

    const layer = new MediaLayer({
      id: layerItem.id,
      source: [layerItem.source],
      title: layerItem.title,
      visible: layerItem.visible,
      opacity: layerItem.opacity,
      position: layerItem.position,
    });

    return layer;
  } catch (err) {
    console.error(err);
  }
}

async function createFeatureLayer(layerItem) {
  try {
    const [FeatureLayer, SpatialReference] = await loadModules([
      "esri/layers/FeatureLayer",
      "esri/geometry/SpatialReference",
    ]);

    let layer;
    if (layerItem.id === "layer-infraestructuras-propias") {
      layer = new FeatureLayer({
        id: layerItem.id,
        source: [],
        title: layerItem.title,
        visible: layerItem.visible,
        position: layerItem.position,
        opacity: layerItem.opacity,
        objectIdField: "ID",
        geometryType: "point",
        spatialReference: SpatialReference.WGS84,
        renderer: await renderInfraestructuraPropiaLayer(),
        labelingInfo: await featureLayerlabels("NOMBRE"),
        labelsVisible: layerItem.labelsVisible,
        outFields: ["*"],
        fields: layerItem.fields,
      });
    } else if (layerItem.id === "layer-viento-lineas") {
      layer = new FeatureLayer({
        id: layerItem.id,
        source: [],
        title: layerItem.title,
        visible: false,
        position: layerItem.position,
        opacity: layerItem.opacity,
        objectIdField: "ID",
        geometryType: "polyline",
        spatialReference: SpatialReference.WGS84,
        fields: layerItem.fields,
        renderer: getWindLineRenderer3D(),
        elevationInfo: {
          mode: "on-the-ground",
        },
        popupTemplate: {
          title: "Viento-Lineas",
          content: (feature) => {
            return `
            <table>
              <tr>
                <td>Dirección</td>
                <td>${feature.graphic.attributes.direction}</td>
              </tr>
              <tr>
                <td>Velocidad</td>
                <td>${feature.graphic.attributes.speed}</td>
              </tr>
            </table>`;
          },
        },
      });
    } else {
      const is3D = store.getters["map/isMap3D"];

      layer = new FeatureLayer({
        id: layerItem.id,
        source: [],
        title: layerItem.title,
        visible: layerItem.visible,
        position: layerItem.position,
        opacity: layerItem.opacity,
        objectIdField: "ID",
        render: {},
        geometryType: "point",
        spatialReference: SpatialReference.WGS84,
        fields: layerItem.fields,
        renderer: is3D ? getWindRenderer3D() : getWindRenderer2D(),
        elevationInfo: {
          mode: "relative-to-ground",
          offset: 50,
          unit: "meters",
        },
        popupTemplate: {
          title: "Viento",
          content: (feature) => {
            return `
            <table>
              <tr>
                <td>Dirección</td>
                <td>${feature.graphic.attributes.direction}</td>
              </tr>
              <tr>
                <td>Velocidad</td>
                <td>${feature.graphic.attributes.speed}</td>
              </tr>
            </table>`;
          },
        },
      });
    }

    return layer;
  } catch (error) {
    console.error("Error creating feature layer", error);
    throw error;
  }
}

async function renderInfraestructuraPropiaLayer() {
  const [UniqueValueRenderer, PictureMarkerSymbol] = await loadModules([
    "esri/renderers/UniqueValueRenderer",
    "esri/symbols/PictureMarkerSymbol",
  ]);

  const renderer = new UniqueValueRenderer({
    field: "TIPO",
    defaultSymbol: new PictureMarkerSymbol({
      url: "icons/base_terrestres.png", // Icono por defecto para todas las infraestructuras
      width: "40px",
      height: "40px",
    }),
  });

  constants.iconosTiposInfraestructuras.forEach(async icono => {
    let value = icono.URL + '.png';
    renderer.addUniqueValueInfo({
      value: icono.TIPO,
      symbol: await createSymbolInfraestructurasPropias('icons/' + value)
    });
  });

  return renderer;
}

async function createSymbolInfraestructurasPropias(icono) {
  const [PictureMarkerSymbol] = await loadModules([
    'esri/symbols/PictureMarkerSymbol'
  ])
  return new PictureMarkerSymbol({
    url: icono, // URL por defecto si no hay coincidencia
    width: '40px',
    height: '40px'
  })
}

function getWindRenderer2D(minSpeed, maxSpeed) {
  return {
    type: "simple",

    symbol: {
      type: "simple-marker",
      path: "m15.707 15.707-3 3a1 1 0 0 1-1.414 0l-3-3a1 1 0 0 1 1.414-1.414L11 15.586V6a1 1 0 0 1 2 0v9.586l1.293-1.293a1 1 0 0 1 1.414 1.414z",
      color: "white",
      angle: 0,
      size: 10,
      outline: {
        color: [255, 255, 255, 0.5],
        width: 0,
      },
    },
    visualVariables: [
      {
        type: "rotation",
        field: "direction",
        rotationType: "geographic",
      },
      {
        type: "color",
        field: "speed",
        stops: [
          { value: minSpeed, color: "#0010d9" },
          { value: maxSpeed / 2, color: "#0080ff" },
          { value: maxSpeed, color: "#00ffff" },
        ],
        valueExpressionTitle: "Intensidad viento",
        valueExpression: "3",
      },

      {
        type: "size",
        field: "speed",
        minDataValue: 0,
        maxDataValue: 5,
        minSize: 17,
        maxSize: 17,
        legendOptions: {
          showLegend: false,
        },
      },
    ],
  };
}

function getWindRenderer3D(minSpeed, maxSpeed) {
  return {
    type: "simple",
    symbol: {
      type: "point-3d",
      symbolLayers: [
        {
          type: "object",
          material: { color: "#a38a8a" },
          resource: { href: "/models/flecha_3D.glb" },
          tilt: 0,
          width: 30,
          depth: 80,
          height: 30,
        },
      ],
    },
    visualVariables: [
      {
        type: "rotation",
        valueExpression: "$feature.direction + 180",
        axis: "heading",
      },
      {
        type: "color",
        field: "speed",
        stops: [
          { value: minSpeed, color: "#0010d9" },
          { value: maxSpeed / 2, color: "#0080ff" },
          { value: maxSpeed, color: "#00ffff" },
        ],
      },
      // LONGITUD de la flecha
      {
        type: "size",
        field: "speed",
        axis: "depth",
        stops: [
          { value: minSpeed, size: 50 },
          { value: maxSpeed, size: 200 },
        ],
      },
      // GROSOR vertical
      {
        type: "size",
        field: "speed",
        axis: "height",
        stops: [
          { value: minSpeed, size: 15 },
          { value: maxSpeed, size: 30 },
        ],
      },
      // ANCHURA lateral
      {
        type: "size",
        field: "speed",
        axis: "width",
        stops: [
          { value: minSpeed, size: 15 },
          { value: maxSpeed, size: 30 },
        ],
      },
    ],
  };
}

function getWindLineRenderer3D(minSpeed, maxSpeed) {
  return {
    type: "simple",
    symbol: {
      type: "line-3d",
      symbolLayers: [
        {
          type: "line",
          size: 2,
          material: { color: "#ffaa00" },
        },
      ],
    },
    visualVariables: [
      {
        type: "color",
        field: "speed",
        stops: [
          { value: minSpeed, color: "#0010d9" },
          { value: maxSpeed / 2, color: "#0080ff" },
          { value: maxSpeed, color: "#00ffff" },
        ],
      },
    ],
  };
}

async function createGeoJsonLayer(layerItem) {
  try {
    const [GeoJSONLayer] = await loadModules(["esri/layers/GeoJSONLayer"]);

    const layer = new GeoJSONLayer({
      url: layerItem.url,
      id: layerItem.id,
      title: layerItem.title,
      visible: layerItem.visible ?? true,
      opacity: layerItem.opacity ?? 1,
      position: layerItem.position,
      legendEnabled: layerItem.legendEnabled ?? true,
      popupEnabled: layerItem.popupEnabled ?? false,
      outFields: ["*"],
    });

    await layer.load();

    const geometryType = layer.geometryType || "polygon";
    layer.renderer = await createCustomGeoJsonRenderer(
      geometryType.toLowerCase(),
      layerItem.color || "black"
    );

    return layer;
  } catch (error) {
    console.error("Error creating GeoJSON layer", error);
    throw error;
  }
}

async function createCustomGeoJsonRenderer(layerGeometryType, color = "black") {
  const [
    SimpleMarkerSymbol,
    SimpleFillSymbol,
    SimpleLineSymbol,
    SimpleRenderer
  ] = await loadModules([
    "esri/symbols/SimpleMarkerSymbol",
    "esri/symbols/SimpleFillSymbol",
    "esri/symbols/SimpleLineSymbol",
    "esri/renderers/SimpleRenderer"
  ]);

  let symbol;

  if (layerGeometryType === "polygon" || layerGeometryType === "multipolygon") {
    symbol = new SimpleFillSymbol({
      color: color,
      outline: new SimpleLineSymbol({
        color: "white",
        width: 1.5
      })
    });
  }

  else if (layerGeometryType === "point" || layerGeometryType === "multipoint") {
    symbol = new SimpleMarkerSymbol({
      style: "circle",
      color: color,
      size: "8px",
      outline: { color: [255, 255, 255], width: 1 }
    });
  }

  else if (layerGeometryType === "polyline") {
    symbol = new SimpleLineSymbol({
      color: color,
      width: 2
    });
  }

  return new SimpleRenderer({
    symbol
  });
}
export const updateEmergenciesLayer = async (mapInstance) => {
  if (!mapInstance) return;

  const layer = mapInstance.findLayerById("layer-emergencias");

  if (!layer) return;

  try {
    const graphicsEmergencias = await createGraphicsEmergencias();

    layer.removeAll();
    layer.addMany(graphicsEmergencias);
  } catch (err) {
    console.error("Error actualizando la capa de emergencias:", err);
  }
};

async function createGraphicsEmergencias() {
  try {
    const [Point] = await loadModules(["esri/geometry/Point"]);
    const emergencias = store.getters["emergencia/emergenciasFiltradas"]; // filtro de emergencias desde la store

    const svgPathIncendio = constants.incendioSVGPath;
    const svgPathEvento = constants.iconoEventoPaths;
    const sources = [];

    for (let i = 0; i < emergencias.length; i++) {
      const emergencia = emergencias[i];

      const color = getEmergenciaColor(emergencia);
      const attributes = {
        ID_EMERGENCIA: emergencia.ID_EMERGENCIA,
        NOMBRE: emergencia.NOMBRE,
        MUNICIPIO: emergencia.MUNICIPIO?.NOMBRE,
        OBSERVACIONES: emergencia.OBSERVACIONES,
        ID_TIPO_DETECCION: emergencia.ID_TIPO_DETECCION,
      };
      const geometry = new Point({
        x: emergencia.LONGITUD,
        y: emergencia.LATITUD,
      });

      if (emergencia.INCENDIO !== null) {
        sources.push({
          // Circulo blanco de fondo
          geometry,
          attributes,
          symbol: {
            type: "simple-marker",
            style: "circle",
            color: "white",
            size: "32px",
            outline: {
              color: "black",
            },
          },
        });
        sources.push({
          // Icono
          geometry,
          attributes,
          symbol: {
            type: "simple-marker",
            path: svgPathIncendio,
            color,
            size: "25px",
            outline: {
              color,
            },
          },
          // popupTemplate: popupTemplate
        });
      } else {
        sources.push({
          // Circulo blanco de fondo
          geometry,
          attributes,
          symbol: {
            type: "simple-marker",
            style: "triangle",
            size: "28px",
            color,
            outline: {
              color: "black",
              width: "6px",
            },
          },
        });
        sources.push({
          // Icono
          geometry,
          attributes,
          symbol: {
            type: "simple-marker",
            path: svgPathEvento,
            size: "35px",
            outline: null,
            color: "white",
          },
          // popupTemplate: popupTemplate
        });
      }

      const nombreEmergencia =
        emergencia.NOMBRE || emergencia.MUNICIPIO?.NOMBRE || "Desconocido";
      const text = wrapText(nombreEmergencia, 16);

      sources.push({
        // Texto
        geometry,
        attributes,
        symbol: {
          type: "text",
          color: "black",
          haloColor: "white",
          haloSize: "2px",
          text,
          xoffset: 0,
          yoffset: -22,
          font: {
            size: 8,
            family: "Arial Unicode MS",
            weight: "bold",
          },
        },
      });
    }
    return sources;
  } catch (err) {
    console.error(err);
  }
  return [];
}

export const drawContorno = async (mapInstance) => {
  const contornoGraphic = await createGraphicsContorno();
  if (!contornoGraphic) return;
  await applyContornoGraphics(mapInstance, contornoGraphic);
};

async function applyContornoGraphics(mapInstance, contornoGraphic) {
  const layerContorno = mapInstance.findLayerById("layer-contorno");
  if (!layerContorno) {
    console.error("No existe layer-contorno en el mapa");
    return;
  }

  layerContorno.removeAll();
  layerContorno.addMany(contornoGraphic);
}

async function createGraphicsContorno() {
  try {
    const rings = store.getters["map/paramsComunidad"]?.RINGS;
    if (!rings) return null;

    const [Graphic, Polygon, SpatialReference] = await loadModules([
      "esri/Graphic",
      "esri/geometry/Polygon",
      "esri/geometry/SpatialReference",
    ]);

    const sr = new SpatialReference({ wkid: 3857 });

    const symbol = {
      type: "simple-fill",
      color: [157, 198, 216, 0.5],
      outline: { color: [157, 198, 216, 1], width: 1 },
    };

    const worldRing = [
      [-20037508.34, -19971868.88],
      [-20037508.34, 19971868.88],
      [20037508.34, 19971868.88],
      [20037508.34, -19971868.88],
      [-20037508.34, -19971868.88],
    ];

    const holeRings = rings.map((ring) => ensureRingOrientation(ring, false));

    const contorno = new Polygon({
      rings: [worldRing, ...holeRings],
      spatialReference: sr,
    });

    return [
      new Graphic({
        geometry: contorno,
        symbol,
      }),
    ];
  } catch (err) {
    console.error("Error creando el contorno:", err);
    return null;
  }
}

function ensureRingOrientation(ring, clockwise = true) {
  if (!Array.isArray(ring) || ring.length < 3) return ring;

  const area = ring.reduce((sum, [x1, y1], index) => {
    const [x2, y2] = ring[(index + 1) % ring.length];
    return sum + (x1 * y2 - x2 * y1);
  }, 0);

  const isClockwise = area < 0;
  return isClockwise === clockwise ? ring : [...ring].reverse();
}

export const deleteLayer = async (mapInstance, layerId) => {
  const layer = mapInstance.findLayerById(layerId);
  if (layer) {
    mapInstance.remove(layer);
  }
};
